import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

const SkillGroup = ({ label, skills, delay }) => (
    <motion.div
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.4, delay }}
    >
        <p className="font-mono text-xs font-medium uppercase tracking-widest text-zinc-400 dark:text-zinc-500 mb-3">
            {label}
        </p>
        <div className="flex flex-wrap gap-2">
            {skills.map((skill, i) => (
                <span key={i} className="tag">{skill}</span>
            ))}
        </div>
    </motion.div>
);

const About = () => {
    const { t } = useTranslation();

    const professional = [
        { label: t('skills.groups.backend'), skills: ['Java', 'Spring Boot', 'Kotlin', 'Quarkus', 'Kafka', 'Node.js', 'GraphQL'] },
        { label: t('skills.groups.frontend'), skills: ['Angular', 'TypeScript', 'JavaScript', 'React', 'Adobe Experience Manager'] },
        { label: t('skills.groups.data'), skills: ['MongoDB', 'MySQL', 'SQL'] },
        { label: t('skills.groups.devops'), skills: ['Docker', 'Git', 'CI/CD', 'Maven', 'Jira', 'New Relic', 'Kibana'] },
    ];

    const personal = [
        { label: t('skills.groups.backend'), skills: ['Cloudflare Workers', 'Hono', 'D1 (SQLite)', 'KV', 'R2'] },
        { label: t('skills.groups.frontend'), skills: ['React', 'Vite', 'Tailwind CSS', 'Framer Motion', 'Recharts'] },
        { label: t('skills.groups.infra'), skills: ['Raspberry Pi', 'Linux', 'Docker', 'Nginx', 'Cloudflare'] },
    ];

    return (
        <section id="about" className="relative section">
            {/* Background orbs for continuity */}
            <div className="absolute inset-0 z-0 pointer-events-none">
                <motion.div 
                    animate={{ x: [0, -40, 0], y: [0, 50, 0] }} 
                    transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut' }}
                    className="absolute top-1/4 -left-20 w-80 h-80 bg-blue-200/20 dark:bg-blue-900/10 rounded-full blur-[100px]"
                />
            </div>

            <div className="container-custom relative z-10">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8 }}
                >
                    {/* Section header */}
                    <div className="mb-12">
                        <span className="section-number">01 — About</span>
                        <h2 className="section-title mt-2">{t('about.sectionTitle')}</h2>
                    </div>

                    {/* Intro Card */}
                    <div className="card p-8 md:p-10 mb-16 bg-white/40 dark:bg-zinc-900/30 border-white/50 dark:border-zinc-800/50 backdrop-blur-xl">
                        <p className="text-lg md:text-xl text-zinc-700 dark:text-zinc-300 leading-relaxed font-light">
                            {t('about.intro')}
                        </p>
                    </div>

                    {/* Skills Section */}
                    <div className="relative">
                        <div className="flex items-center gap-4 mb-10">
                            <span className="section-number mb-0">{t('skills.sectionTitle')}</span>
                            <div className="h-px flex-1 bg-gradient-to-r from-zinc-200 to-transparent dark:from-zinc-800" />
                        </div>

                        <div className="grid md:grid-cols-2 gap-12 lg:gap-16">
                            {/* Professional Skills */}
                            <div className="space-y-8">
                                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-600 dark:text-amber-400">
                                    <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
                                    <h3 className="text-xs font-bold uppercase tracking-widest">
                                        {t('skills.professional')}
                                    </h3>
                                </div>
                                <div className="space-y-6">
                                    {professional.map((group, i) => (
                                        <SkillGroup key={i} label={group.label} skills={group.skills} delay={i * 0.1} />
                                    ))}
                                </div>
                            </div>

                            {/* Personal Skills */}
                            <div className="space-y-8">
                                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-600 dark:text-blue-400">
                                    <span className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse" />
                                    <h3 className="text-xs font-bold uppercase tracking-widest">
                                        {t('skills.personal')}
                                    </h3>
                                </div>
                                <div className="space-y-6">
                                    {personal.map((group, i) => (
                                        <SkillGroup key={i} label={group.label} skills={group.skills} delay={0.2 + i * 0.1} />
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </motion.div>
            </div>
        </section>
    );
};

export default About;
